import { useTranslations } from 'next-intl';
import type { CSSProperties } from 'react';

import DrupalImage from '@commons/components/Image/Drupal';
import TopoPatternImage from '@commons/components/Image/TopoPattern';
import Text from '@commons/components/Text';
import Flex from '@commons/containers/Flex';
import { FEATURED_DISPLAY_IMAGE_SIZES } from '@commons/pagebuilder/nodes/images';

import type { WebinarNodeProps } from '.';
import { WebinarNodeButtonLink } from './GatedDrawer';

/**
 * Webinar node component for the Featured display type.
 */

const WebinarNodeFeatured = (props: WebinarNodeProps) => {
  const t = useTranslations();

  const {
    title,
    description,
    authors,
    image,
    format,
    customLabel,
    customDate,
    ctaLabel,
    alias,
    link,
    isGated,
  } = props;

  const formatLabel = customLabel ? customLabel : format ? format.name : t('nodes.webinar.label');
  const upperTitle = t('commons.featuredType', { type: formatLabel });

  const renderedUpperTitle = (
    <Flex gap="xxs" align="center">
      <Text as="span" level="label-lg" weight="medium" transform="uppercase" marginBottom="none">
        {upperTitle}
      </Text>
      <Text as="span" level="body-sm" marginBottom="none">
        /
      </Text>
      <Text as="span" level="body-sm" transform="uppercase" marginBottom="none">
        {customDate}
      </Text>
    </Flex>
  );

  const imageStyles: CSSProperties = { aspectRatio: '1/1', objectFit: 'cover' };
  const renderedImage = image ? (
    <DrupalImage {...image} sizes={FEATURED_DISPLAY_IMAGE_SIZES} style={imageStyles} />
  ) : (
    <TopoPatternImage alt={title} sizes={FEATURED_DISPLAY_IMAGE_SIZES} style={imageStyles} />
  );

  return (
    <Flex gap="xl" justify="space-between" direction={{ xs: 'column-reverse', md: 'row' }}>
      <Flex gap="md" width={{ xs: 12, md: 6 }} direction="column">
        {renderedUpperTitle}
        <Text as="h2" transform="uppercase" marginBottom="none">
          {title}
        </Text>
        {description && (
          <Text level="body-lg" marginBottom="none">
            {description}
          </Text>
        )}
        {authors && (
          <Text level="body-sm" marginBottom="none">
            {t('commons.by', { author: authors })}
          </Text>
        )}
        <WebinarNodeButtonLink alias={alias} link={link} isGated={isGated} ctaLabel={ctaLabel} />
      </Flex>
      <Flex.Item width={{ xs: 12, md: 6 }}>{renderedImage}</Flex.Item>
    </Flex>
  );
};

export default WebinarNodeFeatured;
